import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, CheckCircle2, XCircle, Loader2, Building2, Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicProposal() {
  const { token } = useParams();
  const [info, setInfo] = useState(undefined); // undefined=loading, null=invalid
  const [status, setStatus] = useState(""); // sent | accepted | declined
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [signName, setSignName] = useState("");
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => {
    axios.get(`${API}/public/proposal/${token}`).then((r) => {
      setInfo(r.data); setStatus(r.data.status || "sent"); setSignName(r.data.name || "");
    }).catch(() => setInfo(null));
  }, [token]);

  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const draw = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#18181A"; ctx.lineTo(x, y); ctx.stroke(); hasDrawn.current = true;
  };
  const stop = () => { drawing.current = false; };
  const clearSig = () => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); hasDrawn.current = false; };

  const act = async (action) => {
    if (action === "accept" && !signName.trim()) { setErr("Please enter your full name to accept."); return; }
    if (!window.confirm(action === "accept" ? "Accept this proposal?" : "Decline this proposal?")) return;
    setBusy(action); setErr("");
    const payload = { action };
    if (action === "accept") {
      payload.signed_name = signName.trim();
      if (hasDrawn.current && canvasRef.current) payload.signature = canvasRef.current.toDataURL("image/png");
    }
    try {
      const { data } = await axios.post(`${API}/public/proposal/${token}`, payload);
      setStatus(data.status);
      if (action === "accept") setInfo((prev) => ({ ...prev, signed_name: payload.signed_name }));
    } catch (e) {
      setErr("Sorry, we couldn't record your response. Please try again.");
    } finally { setBusy(""); }
  };

  if (info === undefined) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (info === null) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] p-6"><p className="text-center text-muted-foreground" data-testid="proposal-invalid">This proposal link is invalid or has expired.</p></div>;

  const responded = status === "accepted" || status === "declined";

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md bg-[#18181A] flex items-center justify-center"><Building2 className="h-5 w-5 text-white" /></div>
          <span className="font-head text-xl font-extrabold tracking-tight">{info.brand_name}</span>
        </div>

        <div className="bg-white border border-border rounded-md p-6" data-testid="proposal-card">
          <p className="overline text-accent">Your proposal</p>
          <h1 className="font-head text-2xl font-extrabold tracking-tight mt-1">Hi {info.name || "there"},</h1>
          <p className="text-sm text-muted-foreground mt-2">Thank you for considering {info.brand_name} to manage your holiday let. Please review the proposal below and let us know your decision.</p>

          {info.message && <p className="text-sm mt-4 whitespace-pre-wrap bg-secondary/40 border border-border rounded-md p-3">{info.message}</p>}

          {info.has_document && (
            <a href={`${API}/public/proposal/${token}/document`} target="_blank" rel="noreferrer" data-testid="proposal-view-doc"
               className="flex items-center gap-2 mt-4 px-4 py-3 border border-border rounded-md hover:bg-secondary/40 transition-colors text-sm font-medium">
              <FileText className="h-4 w-4 text-[#2B4C3F]" />View proposal document
            </a>
          )}

          {responded ? (
            <div className={`mt-6 rounded-md p-4 text-center ${status === "accepted" ? "bg-[#2B4C3F]/[0.06] border border-[#2B4C3F]/25" : "bg-[#9E382A]/[0.06] border border-[#9E382A]/25"}`} data-testid="proposal-result">
              {status === "accepted"
                ? <><CheckCircle2 className="h-8 w-8 text-[#2B4C3F] mx-auto mb-2" /><p className="font-head font-bold text-lg">Proposal accepted</p>{info.signed_name && <p className="text-sm text-muted-foreground mt-1">Signed by {info.signed_name}</p>}<p className="text-sm text-muted-foreground mt-1">Thank you — our team will be in touch shortly to get started.</p></>
                : <><XCircle className="h-8 w-8 text-[#9E382A] mx-auto mb-2" /><p className="font-head font-bold text-lg">Proposal declined</p><p className="text-sm text-muted-foreground mt-1">Thanks for letting us know. We'd love to help in future if things change.</p></>}
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              <div>
                <Label className="overline">Your full name</Label>
                <Input data-testid="proposal-sign-name" value={signName} onChange={(e) => setSignName(e.target.value)} className="mt-1.5" placeholder="Full name" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="overline">Signature (optional)</Label>
                  <button onClick={clearSig} type="button" data-testid="proposal-signature-clear" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"><Eraser className="h-3 w-3" />Clear</button>
                </div>
                <canvas ref={canvasRef} width={440} height={130} data-testid="proposal-signature-pad"
                  className="w-full h-[110px] border border-border rounded-md bg-secondary/20 touch-none cursor-crosshair"
                  onPointerDown={start} onPointerMove={draw} onPointerUp={stop} onPointerLeave={stop} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Button data-testid="proposal-accept" disabled={!!busy || !signName.trim()} onClick={() => act("accept")} className="bg-[#2B4C3F] hover:bg-[#2B4C3F]/90">
                  {busy === "accept" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" />Accept</>}
                </Button>
                <Button data-testid="proposal-decline" variant="outline" disabled={!!busy} onClick={() => act("decline")} className="border-[#9E382A]/40 text-[#9E382A] hover:bg-[#9E382A]/[0.06]">
                  {busy === "decline" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><XCircle className="h-4 w-4 mr-1.5" />Decline</>}
                </Button>
              </div>
            </div>
          )}
          {err && <p className="text-xs text-[#9E382A] text-center mt-3" data-testid="proposal-error">{err}</p>}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-4">Powered by {info.brand_name}</p>
      </div>
    </div>
  );
}
